/**
 * Layer inventory for the comparison view (Wave 5.4). Pure functions.
 *
 * Items describe every toggleable layer the main map can draw (project data, imports,
 * results, rasters, vector tiles, services). Side A is the main map itself, so hiding an item
 * there sets the MapLibre layout visibility of `mainLayerIds`; side B re-renders the same
 * state objects (same GeoJSON, same tile URLs) with per-side `visible` overrides.
 */

export const GROUPS = ['Data', 'Imports', 'Results', 'Rasters', 'Vector tiles', 'Services'];

export function buildCompareItems({
  layers = [], usgsData = {}, importedLayers = [], resultLayers = [], rasterLayers = [], vtLayers = [], serviceLayers = [],
} = {}) {
  const items = [];
  layers.forEach((l) => {
    if (!usgsData?.[l.id]?.features) return; // only layers that actually draw something
    items.push({ key: `usgs:${l.id}`, label: l.name, group: 'Data', mainVisible: !!l.visible,
      mainLayerIds: [`usgs-line-${l.id}`, `usgs-pts-${l.id}`, `usgs-fill-${l.id}`, `usgs-label-${l.id}`] });
  });
  importedLayers.forEach((l) => items.push({ key: `imp:${l.id}`, label: l.name, group: 'Imports', mainVisible: !!l.visible,
    mainLayerIds: [`imp-fill-${l.id}`, `imp-line-${l.id}`, `imp-pts-${l.id}`, `imp-excl-${l.id}`, `imp-label-${l.id}`] }));
  resultLayers.forEach((r) => items.push({ key: `res:${r.id}`, label: r.name, group: 'Results', mainVisible: !!r.visible,
    mainLayerIds: [`result-pts-${r.id}`, `result-fill-${r.id}`, `result-line-${r.id}`, `result-clabel-${r.id}`] }));
  rasterLayers.forEach((r) => items.push({ key: `ras:${r.id}`, label: r.name, group: 'Rasters', mainVisible: !!r.visible,
    mainLayerIds: [`${r.id}-layer-${r.rev}`, `${r.id}-contours`] }));
  vtLayers.forEach((l) => items.push({ key: `vt:${l.id}`, label: l.name, group: 'Vector tiles', mainVisible: !!l.visible,
    mainLayerIds: [l.layerId] }));
  serviceLayers.forEach((s) => items.push({ key: `svc:${s.id}`, label: s.name || s.id, group: 'Services', mainVisible: !!s.visible,
    mainLayerIds: [`layer-${s.id}`, `fill-${s.id}`, `line-${s.id}`, `pts-${s.id}`] }));
  return items;
}

/** Is the item shown on a side? Overrides win; otherwise B follows the main map's state. */
export function isShown(item, overrides = {}) {
  const o = overrides[item.key];
  return o === undefined ? item.mainVisible : !!o;
}

/** Side A can only hide what the main map actually draws (it defaults to shown). */
export function isShownOnMain(item, overridesA = {}) {
  if (!item.mainVisible) return false;
  return overridesA[item.key] !== false;
}

/** The state slices handed to MapDataLayers / VectorTileLayers for side B. */
export function sliceForSide(source, overridesB = {}) {
  const vis = (prefix, x, mainVisible) => isShown({ key: `${prefix}:${x.id}`, mainVisible }, overridesB);
  const mark = (prefix) => (x) => ({ ...x, visible: vis(prefix, x, !!x.visible) });
  const vt = source.vt?.layers
    ? { ...source.vt, popup: null, layers: source.vt.layers.map(mark('vt')) }
    : source.vt;
  return {
    layers: (source.layers || []).map(mark('usgs')),
    importedLayers: (source.importedLayers || []).map(mark('imp')),
    resultLayers: (source.resultLayers || []).map(mark('res')),
    rasterLayers: (source.rasterLayers || []).map(mark('ras')),
    serviceLayers: (source.serviceLayers || []).map(mark('svc')),
    vt,
  };
}

/** Apply side-A visibility to the main map; returns the number of layers changed. */
export function applyMainVisibility(map, items, overridesA = {}) {
  if (!map?.getLayer) return 0;
  let changed = 0;
  items.forEach((item) => {
    const want = overridesA[item.key] === false ? 'none' : 'visible';
    item.mainLayerIds.forEach((id) => {
      if (!map.getLayer(id)) return;
      const cur = map.getLayoutProperty(id, 'visibility') || 'visible';
      if (cur !== want) { map.setLayoutProperty(id, 'visibility', want); changed += 1; }
    });
  });
  return changed;
}

/** Undo every side-A override (used when comparison closes). */
export function restoreMainVisibility(map, items) {
  return applyMainVisibility(map, items, {});
}
